import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import useToken from 'hooks/useToken';

const formatearMoneda = (monto) => {
    if (monto === null || monto === undefined) return '';
    return `$${monto.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatearFecha = (fechaString) => {
    if (!fechaString) return 'N/A';
    const fecha = new Date(fechaString);
    if (isNaN(fecha.getTime())) return fechaString;
    return fecha.toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: '2-digit' }).replace(/ /g, '-');
};

const ModalFacturaConciliada = ({ show, handleClose, movimiento, onDesconciliar }) => {
    const { token } = useToken();
    const [facturas, setFacturas] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show && movimiento && movimiento.id) {
            obtenerFacturas();
        } else {
            setFacturas([]);
            setError(null);
        }
    }, [show, movimiento]);

    const obtenerFacturas = async () => {
        setCargando(true);
        setError(null);
        try {
            const response = await fetch('/api/contabilidad/bancos/Conciliacion/ObtenerFacturasPorMovimiento', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ idMovimientoBancario: movimiento.id })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const parsed = JSON.parse(data.detalles || "[]");
                    setFacturas(parsed);
                } else {
                    setError(data.vMessage || 'No se encontraron facturas conciliadas.');
                }
            } else {
                setError('Error al obtener la información.');
            }
        } catch (err) {
            setError('Error de conexión.');
        } finally {
            setCargando(false);
        }
    };

    if (!movimiento) return null;

    return (
        <Modal show={show} onHide={handleClose} centered backdrop="static">
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="text-primary fs-0">
                    <i className="fas fa-file-invoice-dollar me-2"></i>
                    Facturas Conciliadas
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="text-center mb-3 pb-2 border-bottom border-200">
                    <h6 className="text-700 mb-1 fs--1">
                        Movimiento: {movimiento.descripcion}
                    </h6>
                    <div className="text-500 fs--2 mb-1">Ref: {movimiento.referencia}</div>
                    <div className="d-flex justify-content-center gap-3 text-600 fs--2 fw-semi-bold">
                        <span><i className="far fa-calendar me-1"></i>{formatearFecha(movimiento.fecha)}</span>
                        <span><i className="fas fa-dollar-sign me-1"></i>{formatearMoneda(movimiento.montoOriginal || movimiento.monto)}</span>
                    </div>
                </div>

                {cargando ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" size="sm" />
                        <div className="mt-2 text-500 fs--1">Cargando facturas...</div>
                    </div>
                ) : error ? (
                    <Alert variant="warning" className="fs--1 text-center py-2">
                        {error}
                    </Alert>
                ) : facturas.length === 0 ? (
                    <div className="text-center py-4 text-500 fs--1">
                        No hay facturas asociadas a este movimiento.
                    </div>
                ) : (
                    facturas.map((fac, index) => {
                        let invoiceUrl = '#';
                        if (fac.tipoFactura === 'I' && fac.idFacturaEmitida) {
                            invoiceUrl = `/contabilidad/centro-xml/facturas-emitidas/visualizacion/${fac.idFacturaEmitida}`;
                        } else if (fac.tipoFactura === 'E' && fac.idFacturaRecibida) {
                            invoiceUrl = `/contabilidad/centro-xml/facturas-recibidas/visualizacion/${fac.idFacturaRecibida}`;
                        } else if (fac.tipoFactura === 'P' && fac.idFacturaRecibida) {
                            invoiceUrl = `/contabilidad/centro-xml/pagos-recibidos/visualizacion/${fac.idFacturaRecibida}`;
                        } else if (fac.tipoFactura === 'P' && fac.idFacturaEmitida) {
                            invoiceUrl = `/contabilidad/centro-xml/pagos-emitidos/visualizacion/${fac.idFacturaEmitida}`;
                        }

                        return (
                            <div key={index} className="bg-soft-primary p-2 rounded-3 mb-2 border border-200 fs--1">
                                <Row className="mb-1">
                                    <Col xs={5} className="text-end fw-semi-bold text-600">
                                        Serie y Folio:
                                    </Col>
                                    <Col xs={7} className="fw-bold text-900">
                                        <Link to={invoiceUrl} className="text-decoration-none">
                                            {fac.facturaSerie ? `${fac.facturaSerie}-` : ''}{fac.facturaFolio || 'N/A'}
                                        </Link>
                                    </Col>
                                </Row>
                                <Row className="mb-1">
                                    <Col xs={5} className="text-end fw-semi-bold text-600">
                                        Razón Social:
                                    </Col>
                                    <Col xs={7} className="fw-bold text-900">
                                        <Link to={invoiceUrl} className="text-decoration-none text-900">
                                            {fac.facturaRazonSocial || 'N/A'}
                                        </Link>
                                    </Col>
                                </Row>
                                <Row className="mb-1">
                                    <Col xs={5} className="text-end fw-semi-bold text-600">
                                        RFC:
                                    </Col>
                                    <Col xs={7} className="fw-bold text-900">
                                        {fac.facturaRfc || 'N/A'}
                                    </Col>
                                </Row>
                                <Row className="mb-1">
                                    <Col xs={5} className="text-end fw-semi-bold text-600">
                                        Fecha de Emisión:
                                    </Col>
                                    <Col xs={7} className="fw-bold text-900">
                                        {formatearFecha(fac.facturaFechaEmision)}
                                    </Col>
                                </Row>
                                <Row className="mb-1">
                                    <Col xs={5} className="text-end fw-semi-bold text-600">
                                        Total Factura:
                                    </Col>
                                    <Col xs={7} className="fw-bold text-success fs-0">
                                        {formatearMoneda(fac.facturaTotal)} <span className="fs--2 text-700">{fac.facturaMoneda || 'MXN'}</span>
                                    </Col>
                                </Row>
                                {fac.facturaMoneda && fac.facturaMoneda !== 'MXN' && (
                                    <>
                                        <Row className="mb-1">
                                            <Col xs={5} className="text-end fw-semi-bold text-600">
                                                Tipo de Cambio:
                                            </Col>
                                            <Col xs={7} className="fw-bold text-900">
                                                {fac.facturaTipoCambio ? `$${fac.facturaTipoCambio}` : 'N/A'}
                                            </Col>
                                        </Row>
                                        <Row className="mb-1">
                                            <Col xs={5} className="text-end fw-semi-bold text-600">
                                                Total en MXN:
                                            </Col>
                                            <Col xs={7} className="fw-bold text-success fs-0">
                                                {formatearMoneda(fac.facturaTotal * (fac.facturaTipoCambio || 1))} <span className="fs--2 text-700">MXN</span>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                                <Row className="mb-1">
                                    <Col xs={5} className="text-end fw-semi-bold text-600">
                                        Fecha de Conciliación:
                                    </Col>
                                    <Col xs={7} className="fw-bold text-900">
                                        {formatearFecha(fac.fechaConciliacion)}
                                    </Col>
                                </Row>
                            </div>
                        );
                    })
                )}
                {facturas.length > 0 && (
                    <div className="d-flex justify-content-end mt-2">
                        <Button 
                            variant="outline-danger" 
                            size="sm" 
                            className="py-1 px-3 fs--2 fw-semi-bold"
                            onClick={() => {
                                if (onDesconciliar) {
                                    onDesconciliar(movimiento);
                                    handleClose();
                                }
                            }}
                        >
                            <i className="fas fa-unlink me-1"></i>
                            Desconciliar Todo
                        </Button>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="bg-light">
                <Button variant="secondary" onClick={handleClose}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalFacturaConciliada;
